import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import { AuthLayout } from './AuthLayout';
import { LoaderCenter } from '../../shared/components/Loader';
import { openWebExternal } from './electron-open-web';
import { initElectronDesktopSync, stopElectronSync } from '../../shared/sync-service';
import { api } from '../../shared/api';

// ── Custom desktop session layer ─────────────────────────────────────────────
// Production Clerk can't run in the offline Electron renderer (cross-site FAPI on
// localhost). Instead the user signs in via the system browser (Clerk identity),
// the backend mints a one-use handoff CODE delivered through proclaim://callback,
// and the desktop redeems it for a long-lived TOKEN. That token authenticates VPS
// sync (X-Desktop-Token). Local CRUD needs no token — the embedded server trusts
// its own localhost renderer.

export interface DesktopOrg {
  id: string;
  name: string;
  imageUrl?: string | null;
  role?: string | null;
}

type Status = 'loading' | 'signed-out' | 'signed-in';

interface DesktopAuthValue {
  status: Status;
  token: string | null;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  userImageUrl: string | null;
  orgId: string | null;
  orgName: string | null;
  orgs: DesktopOrg[];
  error: string | null;
  exchanging: boolean; // true while a handoff code is being redeemed
  signIn: () => void;
  signOut: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  refreshOrgs: () => Promise<void>;
}

const Ctx = createContext<DesktopAuthValue | null>(null);

export function useDesktopAuth(): DesktopAuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDesktopAuth must be used within DesktopAuthProvider');
  return v;
}

const LOCAL_ORG = 'local';

export function DesktopAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userImageUrl, setUserImageUrl] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<DesktopOrg[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [exchanging, setExchanging] = useState(false);
  const vpsRef = useRef<string>('');

  const vpsBase = useCallback(async (): Promise<string> => {
    if (!vpsRef.current) {
      vpsRef.current = ((await window.electronAPI?.sync.getVpsUrl()) ?? '').replace(/\/$/, '');
    }
    return vpsRef.current;
  }, []);

  const vpsFetch = useCallback(
    async (path: string, tk: string, init?: RequestInit) => {
      const base = await vpsBase();
      const res = await fetch(`${base}/api${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', 'X-Desktop-Token': tk, ...(init?.headers ?? {}) },
      });
      return res.json() as Promise<{ success: boolean; data?: unknown; error?: string }>;
    },
    [vpsBase],
  );

  // Load identity + org list from a desktop token in one round-trip. Returns false
  // when the token is rejected (expired / revoked / kicked from active org).
  const loadIdentity = useCallback(
    async (tk: string): Promise<boolean> => {
      const me = await vpsFetch('/auth/desktop-me', tk);
      if (!me.success) return false;
      const meData = me.data as {
        userId: string | null;
        orgId: string | null;
        name: string | null;
        email: string | null;
        imageUrl: string | null;
        orgs?: DesktopOrg[];
      };
      setUserId(meData.userId ?? null);
      setUserName(meData.name ?? null);
      setUserEmail(meData.email ?? null);
      setUserImageUrl(meData.imageUrl ?? null);
      const activeOrg = meData.orgId && meData.orgId !== LOCAL_ORG ? meData.orgId : null;
      setOrgId(activeOrg);

      const arr: DesktopOrg[] = meData.orgs ?? [];
      setOrgs(arr);
      setOrgName(arr.find((o) => o.id === activeOrg)?.name ?? null);
      return true;
    },
    [vpsFetch],
  );

  // Boot: restore a stored token (encrypted at rest) and validate it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await window.electronAPI?.auth.getToken();
        if (cancelled) return;
        if (stored && (await loadIdentity(stored))) {
          if (cancelled) return;
          setToken(stored);
          setStatus('signed-in');
          return;
        }
        if (stored) await window.electronAPI?.auth.clearToken();
        if (!cancelled) setStatus('signed-out');
      } catch (e) {
        Sentry.captureException(e, { tags: { stage: 'desktop-boot' } });
        if (!cancelled) setStatus('signed-out');
      }
    })();
    return () => { cancelled = true; };
  }, [loadIdentity]);

  // Refresh identity + orgs whenever the window regains focus. Picks up orgs
  // joined/left in the external browser and detects revoked memberships (Clerk
  // kicks the user → /desktop-me returns 401 → signed out gracefully).
  useEffect(() => {
    if (status !== 'signed-in' || !token) return;
    const onFocus = () => {
      loadIdentity(token).then((ok) => {
        if (!ok) signOutLocal();
      }).catch(() => {/* network blip — ignore */});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // signOutLocal is stable enough — re-binding on every render is fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, token, loadIdentity]);

  // Local sign-out (clears desktop state without hitting VPS again — used when
  // the session was already revoked server-side).
  const signOutLocal = useCallback(() => {
    void window.electronAPI?.auth.clearToken();
    stopElectronSync();
    setToken(null);
    setUserId(null);
    setUserName(null);
    setUserEmail(null);
    setUserImageUrl(null);
    setOrgId(null);
    setOrgName(null);
    setOrgs([]);
    setStatus('signed-out');
  }, []);

  // Redeem a handoff code arriving via the proclaim:// deep link.
  useEffect(() => {
    const unsub = window.electronAPI?.auth.onCode(async (code) => {
      setError(null);
      setExchanging(true);
      try {
        const redeem = await vpsFetch('/auth/desktop-token', '', {
          method: 'POST',
          body: JSON.stringify({ code }),
        });
        if (!redeem.success) {
          setError(redeem.error ?? 'No se pudo canjear el código');
          return;
        }
        const newToken = (redeem.data as { token: string }).token;
        await window.electronAPI?.auth.saveToken(newToken);
        if (await loadIdentity(newToken)) {
          setToken(newToken);
          setStatus('signed-in');
        } else {
          setError('Sesión inválida tras iniciar sesión');
        }
      } catch (e) {
        Sentry.captureException(e, { tags: { stage: 'desktop-code-exchange' } });
        setError('Error al iniciar sesión');
      } finally {
        setExchanging(false);
      }
    });
    return unsub;
  }, [vpsFetch, loadIdentity]);

  const signIn = useCallback(() => {
    window.electronAPI?.auth.openSignIn();
  }, []);

  const signOut = useCallback(async () => {
    try {
      if (token) await vpsFetch('/auth/desktop-signout', token, { method: 'POST' });
    } catch {
      // best-effort revoke
    }
    signOutLocal();
  }, [token, vpsFetch, signOutLocal]);

  const switchOrg = useCallback(
    async (nextOrgId: string) => {
      if (!token || nextOrgId === orgId) return;
      const res = await vpsFetch('/auth/desktop-switch', token, {
        method: 'POST',
        body: JSON.stringify({ orgId: nextOrgId }),
      });
      if (!res.success) {
        setError(res.error ?? 'No se pudo cambiar de organización');
        return;
      }
      setOrgId(nextOrgId);
      setOrgName(orgs.find((o) => o.id === nextOrgId)?.name ?? null);
    },
    [token, orgId, orgs, vpsFetch],
  );

  const refreshOrgs = useCallback(async () => {
    if (!token) return;
    await loadIdentity(token);
  }, [token, loadIdentity]);

  const value: DesktopAuthValue = {
    status,
    token,
    userId,
    userName,
    userEmail,
    userImageUrl,
    orgId,
    orgName,
    orgs,
    error,
    exchanging,
    signIn,
    signOut,
    switchOrg,
    refreshOrgs,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ── Gate: blocks the app until the desktop session is established ─────────────
export function DesktopAuthGate({ children }: { children: ReactNode }) {
  const { status, error, exchanging, signIn } = useDesktopAuth();

  if (status === 'loading') return <LoaderCenter />;
  if (status === 'signed-in') return <>{children}</>;

  return (
    <AuthLayout>
      <div className="w-full max-w-sm flex flex-col items-center gap-5 rounded-2xl border border-stroke-hi bg-surface-card p-8 text-center">
        <div className="flex flex-col gap-1.5">
          <h1 className="font-display font-bold text-lg text-ink">Bienvenido a Proclaim</h1>
          <p className="text-sm text-ink-muted">
            Inicia sesión de forma segura en tu navegador. Al terminar volverás
            automáticamente a la aplicación.
          </p>
        </div>
        <button
          type="button"
          onClick={signIn}
          disabled={exchanging}
          className="w-full rounded-xl bg-accent px-4 py-3 font-medium text-white transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card disabled:opacity-60 disabled:cursor-wait"
        >
          {exchanging ? 'Iniciando sesión…' : 'Iniciar sesión'}
        </button>
        {error && <p className="text-sm text-rose" role="alert">{error}</p>}
      </div>
    </AuthLayout>
  );
}

// ── Bridge: drives offline sync from the desktop session state ───────────────
export function DesktopBridge() {
  const { token, orgId, orgName } = useDesktopAuth();
  const syncedOrg = useRef<string | null>(null);

  useEffect(() => {
    if (!token || !orgId) return;
    if (syncedOrg.current === orgId) return;
    syncedOrg.current = orgId;
    initElectronDesktopSync(token, orgId, orgName ?? undefined).catch((err) =>
      console.error('[DesktopBridge] sync error:', err),
    );
  }, [token, orgId, orgName]);

  // Keep the local org row's display name in sync once known.
  useEffect(() => {
    if (!orgId || !orgName) return;
    api.patchOrg(orgName, orgId).catch(() => {/* non-critical */});
  }, [orgId, orgName]);

  return null;
}

// ── Org guard: requires an active org before the app shell renders ───────────
export function DesktopOrgGuard({ children }: { children: ReactNode }) {
  const { orgId, orgs } = useDesktopAuth();

  if (orgId) return <>{children}</>;

  // No active org. If the user belongs to orgs, let them pick; otherwise send
  // them to the web to create one (Clerk's create/manage UI lives there).
  return (
    <AuthLayout>
      <div className="w-full max-w-sm rounded-2xl border border-stroke-hi bg-surface-card p-6 flex flex-col gap-4 text-center">
        {orgs.length > 0 ? (
          <>
            <h2 className="font-display font-bold text-lg text-ink">Elige una organización</h2>
            <DesktopOrgPicker />
          </>
        ) : (
          <>
            <h2 className="font-display font-bold text-lg text-ink">Un paso más</h2>
            <p className="text-sm text-ink-muted">
              Crea tu iglesia u organización en el navegador para comenzar.
            </p>
            <button
              type="button"
              onClick={() => openWebExternal('/')}
              className="w-full rounded-xl bg-accent px-4 py-3 font-medium text-white hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card transition-colors"
            >
              Crear organización
            </button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}

function DesktopOrgPicker() {
  const { orgs, switchOrg } = useDesktopAuth();
  const [busy, setBusy] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-2">
      {orgs.map((o) => (
        <button
          key={o.id}
          type="button"
          disabled={busy !== null}
          onClick={async () => { setBusy(o.id); try { await switchOrg(o.id); } finally { setBusy(null); } }}
          className="w-full text-left px-3 py-2.5 rounded-xl border border-stroke flex items-center gap-2 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60 transition-colors"
        >
          {o.imageUrl ? (
            <img src={o.imageUrl} alt="" className="w-7 h-7 rounded-md object-cover flex-none" />
          ) : (
            <span className="w-7 h-7 rounded-md bg-accent/10 text-accent text-sm flex items-center justify-center flex-none" aria-hidden="true">
              {o.name.charAt(0).toUpperCase()}
            </span>
          )}
          <span className="truncate text-sm text-ink">{o.name}</span>
          {busy === o.id && <span className="ml-auto text-xs text-ink-muted" aria-live="polite">…</span>}
        </button>
      ))}
    </div>
  );
}
